import {
	Button,
	Divider,
	Image,
	Modal,
	Descriptions,
	message,
	List,
	Avatar,
	Dropdown,
} from "antd";
import {
	EyeOutlined,
	CheckCircleFilled,
	CheckCircleOutlined,
	MinusCircleOutlined,
	EditOutlined,
	DeleteOutlined,
	EllipsisOutlined,
} from "@ant-design/icons";
import * as API from "common/api";
import * as COMMONS from "common/common";
import { TapAnimationComponent } from "components";
import { motion } from "framer-motion";
import moment from "moment";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { DEFAULT_TEXT_NONE_DISPLAY } from "common";
import { isEmpty } from "lodash";
import { useAdminLayoutContext } from "layouts/AdminMain/AdminMainContext";

const MemberActionsDiv = styled.div`
	.ant-dropdown-trigger {
		font-size: 18px;
		margin-right: 1.5em;
		position: relative;
		top: -4px;
	}
`;

const renderDescriptionChildren = (item) => {
	let content = "";
	let textAlignClass = "text-center";
	switch (item.type) {
		case "text":
		case "number":
			content = item?.value || DEFAULT_TEXT_NONE_DISPLAY;
			if (item?.isAddress && content !== DEFAULT_TEXT_NONE_DISPLAY) {
				const prefMatch = String(content).match(/^([1-4]?[0-9])([\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff].*)$/);
				if (prefMatch) {
					const pref = COMMONS.PREFECTURES.find((p) => p.value === Number(prefMatch[1]));
					if (pref) {
						content = `${pref.label}${prefMatch[2]}`;
					}
				}
			}
			break;

		case "radio":
			content = item?.value?.value || DEFAULT_TEXT_NONE_DISPLAY;
			break;

		case "checkbox":
			content =
				(item?.value || []).map(({ value }) => value).join(", ") ||
				DEFAULT_TEXT_NONE_DISPLAY;
			break;

		case "datepicker":
			content = item?.value
				? moment(item.value).format("YYYY-MM-DD")
				: DEFAULT_TEXT_NONE_DISPLAY;
			break;

		case "image":
			const src = item?.value
				? `${import.meta.env.VITE_APP_API_URL}/uploads/members/${item.value}`
				: "/no-image.png";
			return <Image preview={true} className="w-20 h-20" src={src} />;
		default:
			return null;
	}
	if (content.trim() !== DEFAULT_TEXT_NONE_DISPLAY && !item.isCenter) {
		textAlignClass = "text-left-custom";
	}

	return <p className={`text-sm ${textAlignClass}`}>{content}</p>;
};

const DetailModal = (props) => {
	const {
		publicSettings,
		isMemberDetailModalVisible,
		hideMemberDetailModal,
		currentMember,
		customerRegistrations,
		showMemberEditDetailModal,
		handleMemberDelete,
		isShowInOccurence,
	} = props;

	const { isBlock } = useAdminLayoutContext();
	const navigate = useNavigate();
	const isMountedRef = COMMONS.USE_IS_MOUNTED_REF();

	const [campaignQuestions, setCampaignQuestions] = useState([]);
	const [dataMember, setDataMember] = useState([]);

	useQuery(
		[API.QUERY_KEY_ADMIN_CAMPAIGN_QUESTIONS],
		() => API.ADMIN_GET_CAMPAIGN_QUESTIONS(),
		{
			onSuccess: (response) => {
				if (isMountedRef.current) {
					setCampaignQuestions(response?.data || []);
				}
			},
			onError: (error) => {
				if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
					navigate(COMMONS.PERMISSION_ERROR_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
					message.warning({
						content: COMMONS.ERROR_SESSION_MSG,
						key: COMMONS.MESSAGE_SESSION_ERROR_KEY,
					});
					navigate(COMMONS.ADMIN_LOGIN_ROUTE);
				} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				} else {
					message.error({
						content: COMMONS.ERROR_SYSTEM_MSG,
						key: COMMONS.MESSAGE_SYSTEM_ERROR_KEY,
					});
				}
			},
		},
	);

	const isCreatedMannualy =
		!currentMember?.memberCode && !currentMember?.lineId && isShowInOccurence;

	const memberDetailActions = [
		{
			key: "0",
			label: (
				<>
					<EditOutlined className="text-base" />
					<span className="text-base ml-2">編集</span>
				</>
			),
			onClick: showMemberEditDetailModal,
			disabled: isBlock(COMMONS.ACTION_KEY.EDIT_MEMBER),
		},
		{
			key: "1",
			label: (
				<>
					<DeleteOutlined className="text-base" />
					<span className="text-base ml-2">削除</span>
				</>
			),
			onClick: () => handleMemberDelete(currentMember),
			disabled: isBlock(COMMONS.ACTION_KEY.DELETE_MEMBER),
		},
	];

	useEffect(() => {
		let dataCustomerRegistrations = customerRegistrations?.map((item) => {
			const column = `customerRegistrationId${item?.customerRegistrationId}`;
			return {
				...item,
				value: currentMember?.[column],
			};
		});

		if (isCreatedMannualy) {
			dataCustomerRegistrations = dataCustomerRegistrations.filter(
				(it) => it.value,
			);
		}

		setDataMember(dataCustomerRegistrations);
	}, [customerRegistrations, currentMember, isCreatedMannualy]);

	const handleCustomerRegistrations = (item) => {
		return (
			<Descriptions.Item
				className="text-sm"
				label={
					<div
						style={{ maxWidth: 200, wordBreak: "break-all" }}
						className="text-center"
					>
						{item?.label}
					</div>
				}
			>
				{renderDescriptionChildren(item)}
			</Descriptions.Item>
		);
	};

	return (
		<>
			<Modal
				title={
					<MemberActionsDiv className="flex justify-between">
						<span>お客様詳細</span>
						{!isShowInOccurence && (
							<Dropdown
								menu={{ items: memberDetailActions }}
								trigger={["click"]}
								placement="bottomRight"
							>
								<EllipsisOutlined />
							</Dropdown>
						)}
					</MemberActionsDiv>
				}
				open={isMemberDetailModalVisible}
				onCancel={hideMemberDetailModal}
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "hidden",
					},
				}}
				footer={null}
				destroyOnClose
				centered
			>
				<motion.div
					className="flex flex-col justify-center"
					variants={COMMONS.ANIMATION_VARIANT_STAGGER_CONTAINER}
					initial="hidden"
					animate="show"
					exit="hidden"
				>
					<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
						<div className="flex justify-center">
							{currentMember?.picUrl ? (
								<Image
									preview={{
										mask: <EyeOutlined />,
										src: currentMember.picUrl,
										maskClassName: "rounded-full",
									}}
									width={100}
									height={100}
									style={{
										border: `4px solid ${COMMONS.WHITE_COLOR}`,
									}}
									className="rounded-full"
									src={`${currentMember.picUrl}/large`}
									fallback="/no-image.png"
								/>
							) : (
								<Image
									preview={false}
									width={100}
									height={100}
									style={{
										border: `4px solid ${COMMONS.WHITE_COLOR}`,
									}}
									className="rounded-full"
									src="/no-image.png"
								/>
							)}
						</div>
						{isCreatedMannualy && (
							<div className="mb-8 text-center">
								<p className="text-center font-bold text-xl">
									{currentMember?.lastName || DEFAULT_TEXT_NONE_DISPLAY}{" "}
									{currentMember?.firstName || DEFAULT_TEXT_NONE_DISPLAY}様
								</p>
								<p className="text-center text-sm">
									（{currentMember?.lastNameKana || DEFAULT_TEXT_NONE_DISPLAY}{" "}
									{currentMember?.firstNameKana || DEFAULT_TEXT_NONE_DISPLAY}）
								</p>
							</div>
						)}
					</motion.div>
					<motion.div variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}>
						<Descriptions column={1} layout="horizontal" bordered>
							{!isCreatedMannualy && (
								<>
									<Descriptions.Item label="LINE名" className="text-center">
										{currentMember?.displayName || DEFAULT_TEXT_NONE_DISPLAY}
									</Descriptions.Item>
									<Descriptions.Item
										label={<p className="text-center">会員コード</p>}
									>
										{renderDescriptionChildren({
											value: currentMember?.memberCode,
											type: "text",
										})}
									</Descriptions.Item>
								</>
							)}

							{dataMember?.map((item) => handleCustomerRegistrations(item))}

							{isCreatedMannualy ? (
								<>
									<Descriptions.Item label="電話番号" className="text-center">
										{renderDescriptionChildren({
											value: currentMember?.telephone,
											type: "text",
											isCenter: true,
										})}
									</Descriptions.Item>
									<Descriptions.Item
										label="メールアドレス"
										className="text-center"
									>
										{renderDescriptionChildren({
											value: currentMember?.email,
											type: "text",
											isCenter: true,
										})}
									</Descriptions.Item>
									<Descriptions.Item label="住所" className="text-center">
										{renderDescriptionChildren({
											value: `${
												currentMember?.postalCode
													? `〒${COMMONS.POSTAL_CODE_INSERT_CHARACTER(
															currentMember.postalCode,
															3,
															"-",
														)}`
													: `〒${DEFAULT_TEXT_NONE_DISPLAY}`
											} ${currentMember?.address || ""}${
												currentMember?.building || ""
											}`,
											type: "text",
											isCenter: true,
										})}
									</Descriptions.Item>
								</>
							) : (
								<>
									{/* <Descriptions.Item
										label="キャンペーン"
										className="text-center"
									>
										{currentMember?.isCampaign ? (
											currentMember?.candidateAt ? (
												<p>
													<CheckCircleFilled className="text-custom-green mr-1" />
													<span>当選済み</span>
												</p>
											) : (
												<p>
													<CheckCircleOutlined className="text-custom-green mr-1" />
													<span>当選待ち</span>
												</p>
											)
										) : (
											<p className="text-center">
												<MinusCircleOutlined className="text-yellow-600" />
											</p>
										)}
									</Descriptions.Item>
									<Descriptions.Item label="当選⽇" className="text-center">
										{renderDescriptionChildren({
											value: currentMember?.candidateAt
												? moment(currentMember.candidateAt).format("YYYY/M/D")
												: DEFAULT_TEXT_NONE_DISPLAY,
											type: "text",
										})}
									</Descriptions.Item> */}
									<Descriptions.Item label="会員登録日" className="text-center">
										{renderDescriptionChildren({
											value: currentMember?.memberSince
												? moment(currentMember.memberSince).format("YYYY/M/D")
												: DEFAULT_TEXT_NONE_DISPLAY,
											type: "text",
										})}
									</Descriptions.Item>
								</>
							)}
						</Descriptions>
					</motion.div>
					{currentMember?.campaignAnswers &&
					currentMember?.campaignAnswers?.length > 0 ? (
						<>
							<Divider>キャンペーン応募詳細</Divider>
							<List
								dataSource={campaignQuestions}
								itemLayout="horizontal"
								size="small"
								renderItem={(question, i) => (
									<List.Item>
										<List.Item.Meta
											avatar={
												<Avatar
													style={{
														color: COMMONS.WHITE_COLOR,
														backgroundColor: publicSettings?.PRIMARY_COLOR,
													}}
												>
													{i + 1}
												</Avatar>
											}
											title={
												<p className="whitespace-pre-wrap font-bold">
													{question.contents}
												</p>
											}
											description={
												<p>
													{currentMember.campaignAnswers.find(
														(a) =>
															a?.campaignQuestionId ===
															question?.campaignQuestionId,
													)?.contents || DEFAULT_TEXT_NONE_DISPLAY}
												</p>
											}
										/>
									</List.Item>
								)}
							/>
						</>
					) : (
						""
					)}
					<Divider />
					<motion.div
						className="flex justify-center"
						variants={COMMONS.ANIMATION_VARIANT_STAGGER_ITEM}
					>
						<TapAnimationComponent>
							<Button
								size="large"
								className="px-12"
								onClick={hideMemberDetailModal}
							>
								閉じる
							</Button>
						</TapAnimationComponent>
					</motion.div>
				</motion.div>
			</Modal>
		</>
	);
};

export default DetailModal;
